import { z } from "zod";
import type { DialogProps } from "~/components/ui";
import type { ExtractionHint } from "@noa/api-core";
import { inferExtractionSource } from "./pattern-utils";

const fromLlmSchema = z.object({
  prompt_hint: z.string().optional(),
  output_type: z.enum(["string", "int", "bool", "enum", "list[string]"]).optional(),
  // Not editable in the UI, but must survive the edit round-trip
  examples: z.array(z.record(z.string(), z.string())).optional(),
});

// Keys without a dedicated editor are still declared so zodResolver does not
// strip them when editing an existing schema (builtin schemas use them)
const extractionSchema = z.object({
  from_metadata_key: z.string().optional(),
  default_value: z.union([z.string(), z.array(z.string())]).optional(),
  from_path_pattern: z.string().optional(),
  from_path_prefix: z.record(z.string(), z.string()).optional(),
  from_filename_pattern: z.string().optional(),
  from_content_pattern: z.string().optional(),
  path_match_options: z
    .object({
      case_insensitive: z.boolean().optional(),
      url_decode: z.boolean().optional(),
    })
    .optional(),
  from_llm: fromLlmSchema.optional(),
  from_field: z.string().optional(),
  transform: z.string().optional(),
  value_map: z.record(z.string(), z.string()).optional(),
  regex_flags: z.array(z.string()).optional(),
});

const vectorstoreSchema = z.object({
  filterable: z.boolean().optional(),
  searchable: z.boolean().optional(),
  temporal: z.boolean().optional(),
  prompt_template: z.string().optional(),
  embedding_template: z.string().optional(),
});

const fieldSchema = z
  .object({
    name: z.string().min(1, "Field name is required"),
    description: z.string().optional(),
    type: z.string().optional(),
    required: z.boolean().optional(),
    if_present_require: z.array(z.string()).optional(),
    allowed_values: z.array(z.string()).optional(),
    validation_phase: z.enum(["pre_parse", "post_parse"]).optional(),
    extraction: extractionSchema.optional(),
    vectorstore: vectorstoreSchema.optional(),
  })
  // The UI marks these inputs "required" per selected source; without this
  // refine the marks were decorative (everything optional) and a "metadata"
  // extraction without a key silently degraded to an empty "static" one.
  .superRefine((field, ctx) => {
    const source = inferExtractionSource(field.extraction as ExtractionHint | undefined);
    const extraction = field.extraction;
    if (source === "metadata" && !extraction?.from_metadata_key?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["extraction", "from_metadata_key"],
        message: "Metadata key is required for a metadata source",
      });
    }
    if (source === "computed" && !extraction?.from_field?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["extraction", "from_field"],
        message: "Source field is required for a computed source",
      });
    }
    if (source === "llm" && !extraction?.from_llm?.prompt_hint?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["extraction", "from_llm", "prompt_hint"],
        message: "Prompt is required for an LLM source",
      });
    }
  });

const chunkingSchema = z.object({
  strategy: z.string().optional(),
  min_chunk_chars: z.number().optional(),
  items_per_chunk: z.number().min(1).optional(),
});

export const BASE_FIELDS: z.infer<typeof fieldSchema>[] = [
  {
    name: "document_type",
    description: "Type de document pour le filtrage",
    extraction: { default_value: "" },
    vectorstore: { filterable: true },
  },
  {
    name: "sector",
    description: "Code secteur SFP (0000=Bâtiments, 1000=Wabush, 2000=Bloom, 3000=Ferroviaire)",
    extraction: { from_path_pattern: "/(0000|1000|2000|3000)-" },
    vectorstore: { filterable: true },
  },
  {
    name: "site_id",
    description: "Site selon structure de dossiers",
    type: "list[string]",
    extraction: {
      from_path_prefix: {
        "/0000-": "BATIMENTS",
        "/1000-": "WABUSH",
        "/2000-": "BLOOM",
        "/3000-": "FERROVIAIRE",
      },
      default_value: ["BATIMENTS", "BLOOM", "FERROVIAIRE", "WABUSH"],
    },
    vectorstore: { filterable: true, prompt_template: "Site: {value}" },
  },
];

export interface SchemaTemplate {
  id: string;
  name: string;
  description: string;
  schema: Omit<SchemaForm, "schema_id">;
}

export const SCHEMA_TEMPLATES: SchemaTemplate[] = [
  {
    id: "procedure",
    name: "Procédure de maintenance",
    description: "Gamme opérationnelle, job plan, procédure de travail",
    schema: {
      doc_type: "procedure",
      description: "Procédure de maintenance industrielle",
      prompt_hint: "Gamme opérationnelle, procédure de travail, job plan, méthode de maintenance",
      version: 1,
      parser: "PDF",
      extract_page_images: false,
      fields: [
        ...BASE_FIELDS.map((f) => ({
          ...f,
          extraction: {
            ...f.extraction,
            default_value: f.name === "document_type" ? "procedure" : f.extraction?.default_value,
          },
        })),
        {
          name: "procedure_number",
          description: "Numéro de la procédure ou gamme",
          extraction: { from_llm: { prompt_hint: "Extraire le numéro de procédure ou de gamme opérationnelle" } },
          vectorstore: { filterable: true, searchable: true },
        },
        {
          name: "equipment_type",
          description: "Type d'équipement concerné",
          extraction: {
            from_llm: { prompt_hint: "Identifier le type d'équipement ou d'actif concerné par cette procédure" },
          },
          vectorstore: { filterable: true, searchable: true },
        },
        {
          name: "revision",
          description: "Numéro de révision du document",
          extraction: { from_llm: { prompt_hint: "Extraire le numéro de révision" } },
          vectorstore: { filterable: true },
        },
      ],
    },
  },
  {
    id: "safety",
    name: "Document de sécurité",
    description: "Cadenassage, espace clos, permis de travail",
    schema: {
      doc_type: "safety",
      description: "Document de sécurité industrielle",
      prompt_hint: "Procédure de cadenassage, LOTO, espace clos, permis de travail, fiche de sécurité",
      version: 1,
      parser: "PDF",
      extract_page_images: true,
      fields: [
        ...BASE_FIELDS.map((f) => ({
          ...f,
          extraction: {
            ...f.extraction,
            default_value: f.name === "document_type" ? "safety" : f.extraction?.default_value,
          },
        })),
        {
          name: "safety_type",
          description: "Type de document sécurité",
          extraction: {
            from_llm: { prompt_hint: "Identifier le type: cadenassage, espace_clos, permis_travail, fiche_securite" },
          },
          vectorstore: { filterable: true },
        },
        {
          name: "hazards",
          description: "Dangers identifiés",
          type: "list[string]",
          extraction: { from_llm: { prompt_hint: "Lister les dangers ou risques identifiés dans le document" } },
          vectorstore: { searchable: true },
        },
        {
          name: "equipment_id",
          description: "Identifiant de l'équipement concerné",
          extraction: { from_llm: { prompt_hint: "Extraire l'identifiant ou numéro de l'équipement" } },
          vectorstore: { filterable: true, searchable: true },
        },
      ],
    },
  },
  {
    id: "technical_manual",
    name: "Manuel technique",
    description: "Documentation technique, manuel d'opération ou d'entretien",
    schema: {
      doc_type: "manual",
      description: "Manuel technique ou documentation d'équipement",
      prompt_hint: "Manuel technique, documentation d'équipement, guide d'opération, manuel d'entretien",
      version: 1,
      parser: "PDF",
      extract_page_images: true,
      fields: [
        ...BASE_FIELDS.map((f) => ({
          ...f,
          extraction: {
            ...f.extraction,
            default_value: f.name === "document_type" ? "manual" : f.extraction?.default_value,
          },
        })),
        {
          name: "manufacturer",
          description: "Fabricant de l'équipement",
          extraction: { from_llm: { prompt_hint: "Identifier le fabricant ou manufacturier de l'équipement" } },
          vectorstore: { filterable: true, searchable: true },
        },
        {
          name: "model",
          description: "Modèle de l'équipement",
          extraction: { from_llm: { prompt_hint: "Extraire le modèle ou numéro de série de l'équipement" } },
          vectorstore: { filterable: true, searchable: true },
        },
        {
          name: "manual_type",
          description: "Type de manuel",
          extraction: { from_llm: { prompt_hint: "Identifier le type: operation, maintenance, installation, parts" } },
          vectorstore: { filterable: true },
        },
      ],
    },
  },
  {
    id: "engineering",
    name: "Document d'ingénierie",
    description: "Dessin technique, schéma, P&ID, plan",
    schema: {
      doc_type: "engineering",
      description: "Document d'ingénierie ou dessin technique",
      prompt_hint: "Dessin technique, schéma, P&ID, plan d'ingénierie, diagramme",
      version: 1,
      parser: "PDF",
      extract_page_images: true,
      fields: [
        ...BASE_FIELDS.map((f) => ({
          ...f,
          extraction: {
            ...f.extraction,
            default_value: f.name === "document_type" ? "engineering" : f.extraction?.default_value,
          },
        })),
        {
          name: "drawing_number",
          description: "Numéro du dessin ou document",
          extraction: { from_llm: { prompt_hint: "Extraire le numéro de dessin ou de document d'ingénierie" } },
          vectorstore: { filterable: true, searchable: true },
        },
        {
          name: "drawing_type",
          description: "Type de dessin",
          extraction: {
            from_llm: { prompt_hint: "Identifier le type: P&ID, electrical, mechanical, civil, isometric" },
          },
          vectorstore: { filterable: true },
        },
        {
          name: "revision",
          description: "Révision du document",
          extraction: { from_llm: { prompt_hint: "Extraire le numéro de révision" } },
          vectorstore: { filterable: true },
        },
      ],
    },
  },
  {
    id: "blank",
    name: "Schéma vide",
    description: "Commencer avec les champs de base seulement",
    schema: {
      doc_type: "",
      description: "",
      prompt_hint: "",
      version: 1,
      parser: "",
      extract_page_images: false,
      fields: [...BASE_FIELDS],
    },
  },
];

export const schemaFormSchema = z.object({
  schema_id: z
    .string()
    .min(3, "Schema ID must be at least 3 characters")
    .max(64, "Schema ID must be at most 64 characters")
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Must start with lowercase letter, contain only lowercase letters, numbers, and underscores"
    ),
  doc_type: z.string().min(1, "Document type is required"),
  description: z.string().min(1, "Description is required"),
  prompt_hint: z.string().optional().nullable(),
  version: z.number().int().min(1),
  parser: z.string().optional().nullable(),
  extract_page_images: z.boolean(),
  chunking: chunkingSchema.optional(),
  fields: z.array(fieldSchema),
});

export type SchemaForm = z.infer<typeof schemaFormSchema>;

/** Schema dialogs: capped at 90vh, with a scrolling body and a pinned footer. Spread onto `<Dialog>`. */
export const schemaDialogProps: Pick<DialogProps, "className" | "classNames"> = {
  className: "flex max-h-[90vh] flex-col",
  classNames: {
    body: "flex-1 overflow-y-auto pr-[1em] min-h-0",
    footer: "pr-[1em] pt-[0.5em] gap-[1em] flex justify-end flex-shrink-0 border-t border-noa mt-2",
  },
};

export const compactFormItemStyle = "mb-3";
